import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useToast } from "../../contexts/ToastContext";
import { useDebounce } from "./useDebounce";
import {
  fetchRecipients,
  uploadRecipientsCsv,
  deleteRecipient,
  updateRecipient,
} from "../../store/slices/bulkEmailSlice";
import apiService from "../../services/api";

export const useRecipients = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();

  const {
    recipients,
    loading: recipientsLoading,
    error: recipientsError,
    totalCount,
  } = useAppSelector((state: any) => state.bulkEmail.recipients);

  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState<any>(null);
  const [deletingRecipient, setDeletingRecipient] = useState<any>(null);

  // Form states
  const [newRecipient, setNewRecipient] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isAddingRecipient, setIsAddingRecipient] = useState(false);
  const [isImportingCsv, setIsImportingCsv] = useState(false);

  // Debounced search
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Fetch recipients when debounced search term changes
  useEffect(() => {
    dispatch(
      fetchRecipients({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearchTerm,
        status: statusFilter,
      })
    );
  }, [dispatch, debouncedSearchTerm, statusFilter, currentPage, pageSize]);

  // Handlers
  const handleAddRecipient = async () => {
    if (!newRecipient.name.trim() || !newRecipient.email.trim()) {
      showToast("Please fill in all required fields", "warning");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newRecipient.email)) {
      showToast("Please enter a valid email address", "warning");
      return;
    }

    setIsAddingRecipient(true);
    try {
      await apiService.addRecipient({
        ...newRecipient,
        status: "active", // or whatever default status is appropriate
      });

      // Refresh recipients list
      dispatch(
        fetchRecipients({
          page: 1,
          limit: 100,
          search: "",
          status: "all",
        })
      );

      setNewRecipient({ name: "", email: "", phone: "" });
      setShowAddModal(false);
      showToast(
        `Recipient ${newRecipient.name} added successfully!`,
        "success"
      );
    } catch (error: any) {
      showToast(`Failed to add recipient: ${error.message}`, "error");
    } finally {
      setIsAddingRecipient(false);
    }
  };

  const handleCsvImport = async () => {
    if (!csvFile) {
      showToast("Please select a CSV file", "warning");
      return;
    }

    // Check file size (200MB limit)
    const maxSize = 200 * 1024 * 1024; // 200MB
    if (csvFile.size > maxSize) {
      showToast(
        "File too large. Maximum size is 200MB. Please use a smaller file or split your data.",
        "error"
      );
      return;
    }

    // Check file type
    if (!csvFile.name.toLowerCase().endsWith(".csv")) {
      showToast(
        "Please select a CSV file (.csv extension required)",
        "warning"
      );
      return;
    }

    setIsImportingCsv(true);
    try {
      await dispatch(uploadRecipientsCsv(csvFile));

      // Refresh recipients after successful upload
      dispatch(
        fetchRecipients({
          page: 1,
          limit: 100,
          search: "",
          status: "all",
        })
      );

      setCsvFile(null);
      setShowImportModal(false);
      showToast("CSV file uploaded and processed successfully!", "success");
    } catch (error: any) {
      showToast(
        `Failed to upload CSV: ${error.message || "Unknown error"}`,
        "error"
      );
    } finally {
      setIsImportingCsv(false);
    }
  };

  const handleToggleSelect = (recipientId: string) => {
    setSelectedRecipients((prev) =>
      prev.includes(recipientId)
        ? prev.filter((id) => id !== recipientId)
        : [...prev, recipientId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRecipients.length === recipients.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(recipients.map((r: any) => r.id));
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRefresh = () => {
    dispatch(
      fetchRecipients({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearchTerm,
        status: statusFilter,
      })
    );
  };

  const handleEditRecipient = (recipient: any) => {
    setEditingRecipient(recipient);
    setShowEditModal(true);
  };

  const handleDeleteRecipient = (recipient: any) => {
    setDeletingRecipient(recipient);
    setShowDeleteModal(true);
  };

  const handleUpdateRecipient = async (recipientData: any) => {
    if (!editingRecipient) return;

    try {
      await dispatch(
        updateRecipient({
          id: editingRecipient.id,
          ...recipientData,
        })
      ).unwrap();
      showToast("Recipient updated successfully", "success");
      setShowEditModal(false);
      setEditingRecipient(null);
      // Refresh the recipients list
      dispatch(
        fetchRecipients({
          page: currentPage,
          limit: pageSize,
          search: debouncedSearchTerm,
          status: statusFilter,
        })
      );
    } catch (error: any) {
      showToast(error.message || "Failed to update recipient", "error");
    }
  };

  const handleConfirmDeleteRecipient = async (recipientId: string) => {
    try {
      await dispatch(deleteRecipient(recipientId)).unwrap();
      showToast("Recipient deleted successfully", "success");
      setShowDeleteModal(false);
      setDeletingRecipient(null);
      // Refresh the recipients list
      dispatch(
        fetchRecipients({
          page: currentPage,
          limit: pageSize,
          search: debouncedSearchTerm,
          status: statusFilter,
        })
      );
    } catch (error: any) {
      showToast(error.message || "Failed to delete recipient", "error");
    }
  };

  return {
    // Data
    recipients: recipients || [],
    recipientsLoading,
    recipientsError,
    totalCount: totalCount || 0,

    // Search and filters
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    debouncedSearchTerm,

    // Pagination
    currentPage,
    pageSize,
    setPageSize,

    // Selection
    selectedRecipients,
    setSelectedRecipients,

    // Modal states
    showAddModal,
    setShowAddModal,
    showImportModal,
    setShowImportModal,
    showEditModal,
    setShowEditModal,
    showDeleteModal,
    setShowDeleteModal,
    editingRecipient,
    setEditingRecipient,
    deletingRecipient,
    setDeletingRecipient,

    // Form states
    newRecipient,
    setNewRecipient,
    csvFile,
    setCsvFile,
    isAddingRecipient,
    isImportingCsv,

    // Handlers
    handleAddRecipient,
    handleCsvImport,
    handleToggleSelect,
    handleSelectAll,
    handlePageChange,
    handleRefresh,
    handleEditRecipient,
    handleDeleteRecipient,
    handleUpdateRecipient,
    handleConfirmDeleteRecipient,
  };
};
